import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme, useMediaQuery } from '@mui/material';
import { ProfessionalSidebar } from './ProfessionalSidebar';
import { MobileHeader } from './MobileHeader';

// Unified Professional Layout CSS-in-JS styles
const styles = {
  // CSS Variables matching the VRP system
  vars: {
    '--vrp-primary': '#065f46',     // Deep Forest Green - PRIMARY
    '--vrp-secondary': '#7dd3fc',   // Cyan Blue - MODERN ACCENT  
    '--vrp-accent': '#10b981',      // Bright Green - ENERGY/ACTION
    '--vrp-navy': '#1e293b',       // Dark Navy - BACKGROUND
    '--surface-primary': '#ffffff',    // Pure white
    '--surface-secondary': '#f8fafc',  // Very light gray
    '--surface-tertiary': '#f1f5f9',   // Subtle gray
    '--text-primary': '#0f172a',      // Very dark slate
    '--text-secondary': '#475569',    // Medium slate
    '--text-tertiary': '#94a3b8',     // Light slate
    '--border-subtle': '#e2e8f0',
    '--text-xs': '0.75rem',
    '--text-sm': '0.875rem',
    '--text-base': '1rem',
    '--text-lg': '1.125rem',
    '--text-xl': '1.25rem',
    '--text-2xl': '1.5rem',
    '--weight-normal': '400',
    '--weight-medium': '500',
    '--weight-semibold': '600',
    '--weight-bold': '700',
    '--space-1': '0.25rem',
    '--space-2': '0.5rem',
    '--space-3': '0.75rem',
    '--space-4': '1rem',
    '--space-6': '1.5rem',
    '--space-8': '2rem',
    '--radius-md': '0.375rem',
    '--radius-lg': '0.5rem',
    '--radius-xl': '0.75rem',
    '--radius-2xl': '1rem',
    '--radius-full': '9999px',
    '--duration-fast': '0.15s',
    '--duration-normal': '0.25s',
    '--ease-out-quart': 'cubic-bezier(0.25, 1, 0.5, 1)',
  },

  // Main layout wrapper
  appLayout: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    backgroundColor: 'var(--surface-primary)',
    color: 'var(--text-primary)',
    lineHeight: '1.5',
  },

  // Top ribbon
  topRibbon: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    height: '48px',
    background: 'var(--surface-secondary)',
    borderBottom: '1px solid var(--border-subtle)',
    zIndex: 110,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 var(--space-4)',
  },

  ribbonLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-4)',
  },

  ribbonRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
  },

  logo: {
    width: '32px',
    height: '32px',
    marginRight: 'var(--space-3)',
    background: 'var(--vrp-primary)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'var(--weight-bold)',
    fontSize: 'var(--text-sm)',
  },

  // Hover sidebar - collapsed by default, expand on hover
  sidebar: {
    position: 'fixed' as const,
    left: 0,
    top: '48px',
    height: 'calc(100vh - 48px)',
    width: '64px', // Collapsed width
    background: 'var(--surface-secondary)',
    borderRight: '1px solid var(--border-subtle)',
    padding: 'var(--space-6) 0',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    transition: 'width var(--duration-normal) var(--ease-out-quart)',
    zIndex: 100,
  },

  sidebarExpanded: {
    width: '240px', // Expanded width on hover
  },

  navSection: {
    marginBottom: 'var(--space-8)',
    padding: '0 var(--space-2)', // Adjusted for collapsed state
  },

  navSectionTitle: {
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--weight-semibold)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: 'var(--space-3)',
    padding: '0 var(--space-2)',
    opacity: 0, // Hidden when collapsed
    transition: 'opacity var(--duration-normal) var(--ease-out-quart)',
    whiteSpace: 'nowrap' as const,
  },

  navSectionTitleExpanded: {
    opacity: 1, // Visible when expanded
  },

  navItem: {
    display: 'flex',
    alignItems: 'center',
    padding: 'var(--space-3) var(--space-2)',
    margin: 'var(--space-1) 0',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--ease-out-quart)',
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    position: 'relative',
  },

  navItemActive: {
    background: 'color-mix(in srgb, var(--vrp-primary) 15%, transparent)',
    color: 'var(--vrp-primary)',
    borderLeft: '3px solid var(--vrp-primary)',
    marginLeft: '0px',
  },

  navIcon: {
    width: '20px',
    height: '20px',
    marginRight: 'var(--space-3)',
    fill: 'currentColor',
    flexShrink: 0,
  },

  navText: {
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-medium)',
    opacity: 0, // Hidden when collapsed
    transition: 'opacity var(--duration-normal) var(--ease-out-quart)',
    whiteSpace: 'nowrap' as const,
  },

  navTextExpanded: {
    opacity: 1, // Visible when expanded
  },

  // Main content - adjusts based on sidebar state and screen size
  mainContent: {
    marginLeft: '64px', // Account for collapsed sidebar on desktop
    padding: 'var(--space-6)',
    width: 'calc(100% - 64px)',
    minHeight: '100vh',
    paddingTop: '48px', // Just the ribbon height, no extra spacing
    transition: 'margin-left var(--duration-normal) var(--ease-out-quart), width var(--duration-normal) var(--ease-out-quart)',
  },

  // MPAD Mobile Enhancement: Responsive main content styles
  mainContentMobile: {
    marginLeft: 0, // No sidebar margin on mobile
    padding: 'var(--space-4)',
    width: '100%',
    minHeight: '100vh',
    paddingTop: '104px', // 48px ribbon + 56px mobile header
    transition: 'margin-left var(--duration-normal) var(--ease-out-quart), width var(--duration-normal) var(--ease-out-quart)',
  },

  mainContentTablet: {
    marginLeft: '64px', // Collapsed sidebar on tablet
    padding: 'var(--space-5)',
    width: 'calc(100% - 64px)',
    minHeight: '100vh',
    paddingTop: '48px',
    transition: 'margin-left var(--duration-normal) var(--ease-out-quart), width var(--duration-normal) var(--ease-out-quart)',
  },

  mainContentDesktop: {
    marginLeft: '240px', // Full sidebar on desktop
    padding: 'var(--space-6)',
    width: 'calc(100% - 240px)',
    minHeight: '100vh',
    paddingTop: '48px',
    transition: 'margin-left var(--duration-normal) var(--ease-out-quart), width var(--duration-normal) var(--ease-out-quart)',
  },

  // Page header
  pageHeader: {
    marginBottom: 'var(--space-6)',
  },

  pageTitle: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 'var(--weight-semibold)',
    color: 'var(--text-primary)',
    marginBottom: 'var(--space-1)',
  },

  pageSubtitle: {
    fontSize: 'var(--text-sm)',
    color: 'var(--text-secondary)',
  },

  // Action bar
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--space-4)', // Reduced margin
    paddingTop: 'var(--space-2)', // Small padding from top
  },

  tabs: {
    borderBottom: '1px solid var(--border-subtle)',
    marginBottom: 'var(--space-4)', // Reduced margin
    position: 'sticky' as const,
    top: '48px', // Stick to the ribbon
    backgroundColor: 'var(--surface-primary)',
    zIndex: 100,
  },

  tabList: {
    display: 'flex',
    gap: 'var(--space-8)',
  },

  tab: {
    padding: 'var(--space-4) 0',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-medium)',
    color: 'var(--text-secondary)',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--ease-out-quart)',
  },

  tabActive: {
    color: 'var(--vrp-primary)',
    borderBottomColor: 'var(--vrp-primary)',
  },

  actionGroup: {
    display: 'flex',
    gap: 'var(--space-3)',
  },

  // Buttons
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-3) var(--space-6)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-medium)',
    transition: 'all var(--duration-fast) var(--ease-out-quart)',
    cursor: 'pointer',
    border: 'none',
    textDecoration: 'none',
  },

  btnPrimary: {
    background: 'var(--vrp-primary)',
    color: 'white',
  },

  btnSecondary: {
    background: 'var(--surface-tertiary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-subtle)',
  },

  btnGhost: {
    background: 'transparent',
    color: 'var(--text-secondary)',
  },

  userMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-1) var(--space-2)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--ease-out-quart)',
  },

  userAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'var(--vrp-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--weight-semibold)',
  },

  dropdown: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    marginTop: 'var(--space-1)',
    background: 'var(--surface-primary)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid var(--border-subtle)',
    minWidth: '200px',
    zIndex: 1000,
    overflow: 'hidden',
  },

  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-3) var(--space-4)',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    transition: 'background-color var(--duration-fast)',
    borderBottom: '1px solid var(--surface-tertiary)',
  },

  dropdownItemHover: {
    backgroundColor: 'var(--surface-secondary)',
  },

  dropdownHeader: {
    padding: 'var(--space-4)',
    borderBottom: '1px solid var(--border-subtle)',
    backgroundColor: 'var(--surface-secondary)',
  },

  dropdownUserInfo: {
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--weight-medium)',
    color: 'var(--text-primary)',
    marginBottom: 'var(--space-1)',
  },

  dropdownUserEmail: {
    fontSize: 'var(--text-xs)',
    color: 'var(--text-tertiary)',
  },

  dropdownIcon: {
    width: '16px',
    height: '16px',
    fill: 'currentColor',
  },
};

interface ProfessionalLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  pageSubtitle?: string;
  showTabs?: boolean;
  activeTab?: string;
  actionButtons?: React.ReactNode;
}

export const ProfessionalLayout: React.FC<ProfessionalLayoutProps> = ({
  children,
  pageTitle = "VRP System",
  pageSubtitle = "Vehicle Routing Problem Solution",
  showTabs = true,
  activeTab,
  actionButtons
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Extract project ID from URL for project-aware navigation
  const getProjectIdFromUrl = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'projects' && pathParts[2]) {
      const id = parseInt(pathParts[2], 10);
      return !isNaN(id) ? id : null;
    }
    return null;
  };
  const currentProjectId = getProjectIdFromUrl();
  const { user, signOut } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // MPAD Mobile Enhancement: Add responsive breakpoints and mobile state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 768px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 768px - 1024px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // ≥ 1024px
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Handle mobile drawer toggle
  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Helper functions for user display
  const getUserInitials = () => {
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    return user?.email || 'User';
  };

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    await signOut();
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuOpen && event.target) {
        const target = event.target as Element;
        if (!target.closest('[data-user-menu]')) {
          setUserMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Apply CSS variables scoped to this component
  useEffect(() => {
    const styleElement = document.createElement('style');
    const cssVariables = Object.entries(styles.vars)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n    ');
    
    styleElement.textContent = `
      .professional-layout-scope {
        ${cssVariables}
      }
      
      .professional-layout-scope button:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      
      .professional-layout-scope .btn-primary:hover:not(:disabled) {
        background: color-mix(in srgb, var(--vrp-primary) 90%, black) !important;
      }
      
      .professional-layout-scope .btn-secondary:hover:not(:disabled) {
        background: var(--surface-secondary) !important;
        border-color: var(--vrp-primary) !important;
      }
      
      .professional-layout-scope .btn-ghost:hover:not(:disabled) {
        background: var(--surface-tertiary) !important;
        color: var(--text-primary) !important;
      }
      
      /* Hide sidebar scrollbar while maintaining scroll functionality */
      .professional-layout-scope nav::-webkit-scrollbar {
        display: none;
      }
      
      /* Navbar hover effects */
      .professional-layout-scope .nav-item:hover {
        background: var(--surface-tertiary);
      }
      
      /* User menu hover effects */
      .professional-layout-scope .user-menu:hover {
        background: var(--surface-tertiary);
      }
      
      .professional-layout-scope .dropdown-item:hover {
        background: var(--surface-secondary) !important;
      }
    `;
    
    document.head.appendChild(styleElement);

    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  // Determine active navigation item
  const getActiveNavItem = () => {
    const path = location.pathname;
    if (path.includes('/projects')) return 'projects';
    if (path.includes('/scenarios')) return 'scenarios';
    if (path.includes('/data-management')) return 'data-management';
    if (path.includes('/locations')) return 'locations';
    if (path.includes('/enhanced-vehicles')) return 'enhanced-vehicles';
    if (path.includes('/vehicles')) return 'vehicles';
    if (path.includes('/jobs')) return 'jobs';
    if (path.includes('/vehicle-summary')) return 'vehicle-summary';
    if (path.includes('/optimization-results')) return 'optimization-results';
    if (path.includes('/optimization')) return 'optimization';
    if (path.includes('/map')) return 'map';
    if (path.includes('/schedule')) return 'schedule';
    if (path.includes('/dashboard')) return 'dashboard';
    return 'dashboard';
  };

  const activeNavItem = getActiveNavItem();

  // Determine active tab based on current location if not explicitly provided
  const getActiveTab = () => {
    if (activeTab) return activeTab;
    
    const path = location.pathname;
    if (path.includes('/projects')) return 'Projects';
    if (path.includes('/scenarios')) return 'Scenarios';
    if (path.includes('/data-management')) return 'Data Management';
    return 'Projects'; // Default
  };

  const currentActiveTab = getActiveTab();

  const renderTopRibbon = () => (
    <div style={styles.topRibbon}>
      <div style={styles.ribbonLeft}>
        <div style={styles.logo}>V</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-semibold)', color: 'var(--vrp-primary)' }}>
            VRP System
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>/</span>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            {pageTitle}
          </span>
        </div>
      </div>
      <div style={styles.ribbonRight}>
        <button 
          style={{ ...styles.btn, ...styles.btnGhost, padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)' }}
          onClick={() => window.open('https://docs.vrpsystem.com', '_blank')}
        >
          Docs
        </button>
        <button 
          style={{ ...styles.btn, ...styles.btnGhost, padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)' }}
          onClick={() => window.open('mailto:support@vrpsystem.com')}
        >
          Support
        </button>
        <div 
          style={{ ...styles.userMenu, position: 'relative' }}
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          className="user-menu"
          data-user-menu
        >
          <div style={styles.userAvatar}>{getUserInitials()}</div>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            {getDisplayName()}
          </span>
          <svg 
            style={{ 
              width: '16px', 
              height: '16px', 
              marginLeft: 'var(--space-1)',
              transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform var(--duration-fast)'
            }} 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
          
          {userMenuOpen && (
            <div style={styles.dropdown} data-user-menu>
              <div style={styles.dropdownHeader}>
                <div style={styles.dropdownUserInfo}>{getDisplayName()}</div>
                <div style={styles.dropdownUserEmail}>{user?.email}</div>
              </div>
              
              <div 
                style={styles.dropdownItem}
                className="dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                  // Navigate to profile/settings when implemented
                  console.log('Navigate to profile');
                }}
              >
                <svg style={styles.dropdownIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z" />
                </svg>
                Profile Settings
              </div>
              
              <div 
                style={styles.dropdownItem}
                className="dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                  // Navigate to account settings when implemented
                  console.log('Navigate to account settings');
                }}
              >
                <svg style={styles.dropdownIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.205 1.251l-1.18 2.044a1 1 0 01-1.186.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.205-1.251l1.18-2.044a1 1 0 011.186-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Account Settings
              </div>
              
              <div 
                style={styles.dropdownItem}
                className="dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://docs.vrpsystem.com', '_blank');
                  setUserMenuOpen(false);
                }}
              >
                <svg style={styles.dropdownIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                </svg>
                Documentation
              </div>
              
              <div 
                style={{
                  ...styles.dropdownItem,
                  borderBottom: 'none',
                  color: 'var(--text-secondary)',
                }}
                className="dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSignOut();
                }}
              >
                <svg style={styles.dropdownIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
                Sign Out
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Generate project-aware URLs
  const getProjectAwareUrl = (basePath: string) => {
    if (currentProjectId && basePath !== '/dashboard' && basePath !== '/projects') {
      return `/projects/${currentProjectId}${basePath}`;
    }
    return basePath;
  };

  // MPAD Mobile Enhancement: Old sidebar removed - now using ProfessionalSidebar component

  const renderUnifiedTabs = () => {
    if (!showTabs) return null;

    // Generate project-aware tab routes
    const getTabRoute = (tabName: string) => {
      if (tabName === 'Projects') return '/projects';
      if (tabName === 'Scenarios') {
        const url = getProjectAwareUrl('/scenarios');
        return url === '/scenarios' ? '/projects' : url;
      }
      return '/projects';
    };

    // Only show top-level tabs, not ones duplicated in sidebar
    const tabs = [
      { name: 'Projects', route: getTabRoute('Projects') },
      { name: 'Scenarios', route: getTabRoute('Scenarios') }
    ];

    return (
      <div style={styles.actionBar}>
        <div style={styles.tabs}>
          <div style={styles.tabList}>
            {tabs.map((tab) => (
              <div
                key={tab.name}
                style={{
                  ...styles.tab,
                  ...(currentActiveTab === tab.name ? styles.tabActive : {}),
                }}
                onClick={() => navigate(tab.route)}
              >
                {tab.name}
              </div>
            ))}
          </div>
        </div>
        
        {/* Action Buttons */}
        {actionButtons && (
          <div style={styles.actionGroup}>
            {actionButtons}
          </div>
        )}
      </div>
    );
  };

  // MPAD Hover Enhancement: Get responsive main content styles
  const getMainContentStyles = () => {
    if (isMobile) return styles.mainContentMobile;
    if (isTablet) return styles.mainContentTablet;
    // Desktop always uses collapsed sidebar (64px) since hover expansion doesn't affect layout
    return styles.mainContent;
  };

  return (
    <div style={styles.appLayout} className="professional-layout-scope">
      {renderTopRibbon()}
      
      {/* MPAD Mobile Enhancement: Mobile Header */}
      <MobileHeader
        onMenuToggle={handleMobileDrawerToggle}
        title={pageTitle}
        subtitle={pageSubtitle}
      />
      
      {/* MPAD Hover Enhancement: Responsive Sidebar with hover-based expansion */}
      <ProfessionalSidebar
        currentPage={activeNavItem}
        onNavigate={(page) => {
          const url = getProjectAwareUrl(`/${page}`);
          navigate(url);
        }}
        mobileOpen={mobileDrawerOpen}
        onMobileToggle={handleMobileDrawerToggle}
      />
      
      <main style={getMainContentStyles()}>
        {/* Unified Tabs - Only show on desktop/tablet */}
        {!isMobile && renderUnifiedTabs()}

        {/* Main Content */}
        {children}
      </main>
    </div>
  );
};

export default ProfessionalLayout;